import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

// Event types matching backend
export const EventTypes = {
  PRODUCT_CREATED: 'product_created',
  PRODUCT_UPDATED: 'product_updated',
  PRODUCT_DELETED: 'product_deleted',
  STOCK_MOVED: 'stock_moved',
  STOCK_TRANSFERRED: 'stock_transferred',
  STOCK_ADJUSTED: 'stock_adjusted',
  LOCATION_CREATED: 'location_created',
  LOCATION_UPDATED: 'location_updated',
  LOCATION_DELETED: 'location_deleted',
  TRANSACTION_CREATED: 'transaction_created',
  TRANSACTION_UPDATED: 'transaction_updated',
  DASHBOARD_REFRESH: 'dashboard_refresh',
  REPORTS_REFRESH: 'reports_refresh',
  DATA_REFRESH: 'data_refresh'
};

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const listenersRef = useRef(new Map());
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000;

  // Get WebSocket URL from environment
  const getWsUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    // Convert http(s) to ws(s)
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    return `${wsProtocol}://${wsHost}/api/ws`;
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const wsUrl = getWsUrl();
      console.log('[WebSocket] Connecting to:', wsUrl);
      
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('[WebSocket] Connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      wsRef.current.onmessage = (event) => {
        // Short-circuit non-JSON keepalive frames to avoid console parse spam
        if (typeof event.data === 'string' && (event.data === 'pong' || event.data === 'ping')) {
          return;
        }
        try {
          const message = JSON.parse(event.data);
          // Note: dropped per-message console.log on purpose. With many
          // concurrent users + activity logs this fired dozens of times
          // per minute and noticeably slowed the browser.
          setLastMessage(message);
          
          // Notify all listeners for this event type
          const typeListeners = listenersRef.current.get(message.type) || [];
          typeListeners.forEach(callback => {
            try {
              callback(message.data);
            } catch (err) {
              console.error('[WebSocket] Listener error:', err);
            }
          });
          
          // Also notify "all" listeners
          const allListeners = listenersRef.current.get('*') || [];
          allListeners.forEach(callback => {
            try {
              callback(message);
            } catch (err) {
              console.error('[WebSocket] All-listener error:', err);
            }
          });
        } catch (err) {
          console.error('[WebSocket] Parse error:', err);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('[WebSocket] Disconnected:', event.code, event.reason);
        setIsConnected(false);
        
        // Attempt reconnect with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(
            baseReconnectDelay * Math.pow(2, reconnectAttempts.current),
            30000 // Max 30 seconds
          );
          console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
      };
    } catch (err) {
      console.error('[WebSocket] Connection error:', err);
    }
  }, [getWsUrl]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Subscribe to specific event type
  const subscribe = useCallback((eventType, callback) => {
    if (!listenersRef.current.has(eventType)) {
      listenersRef.current.set(eventType, []);
    }
    listenersRef.current.get(eventType).push(callback);
    
    // Return unsubscribe function
    return () => {
      const listeners = listenersRef.current.get(eventType) || [];
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  // Send ping to keep connection alive
  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send('ping');
    }
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();
    
    // Setup ping interval for keep-alive
    const pingInterval = setInterval(sendPing, 30000);
    
    return () => {
      clearInterval(pingInterval);
      disconnect();
    };
  }, [connect, disconnect, sendPing]);

  // Handle visibility change - reconnect when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnected) {
        console.log('[WebSocket] Tab visible, reconnecting...');
        reconnectAttempts.current = 0;
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isConnected, connect]);

  const value = {
    isConnected,
    lastMessage,
    subscribe,
    connect,
    disconnect
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook for subscribing to real-time updates
export const useRealTimeUpdates = (eventTypes, onUpdate, deps = []) => {
  const { subscribe, isConnected } = useWebSocket();
  
  useEffect(() => {
    if (!isConnected) return;
    
    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
    const unsubscribes = types.map(type => subscribe(type, onUpdate));
    
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, subscribe, ...deps]);
};

export default WebSocketContext;
